import { catchError, debounceTime, flatMap, switchMap } from "rxjs/operators"
import { combineEpics, ofType, StateObservable } from "redux-observable"
import { concat, EMPTY, from, of } from "rxjs"
import { Epic } from "../../epics"

import * as actions from "./actions"
import { addErrorNotification } from "../notifications/actions"
import { GlobalState } from "../../store"
import { getWorkspaceId } from "../../util/GetWorkspaceId"
import { RequestCancelledError } from "../../errors"
import { IndicatorsResponse } from "../../apis/models/indicator"
import { IIndicatorData } from "./index"

export const defaultGetIndicatorsErrorMessage =
    "Something went wrong when fetching indicators."
export const defaultGetIndicatorErrorMessage =
    "Something went wrong when fetching the indicator."

export const requestIndicatorsEpic: Epic = (
    action$,
    state$: StateObservable<GlobalState>,
    { indicators: { indicatorAPI, loadingDelay } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_INDICATORS),
        debounceTime(loadingDelay),
        switchMap(() => {
            const workspaceId = getWorkspaceId()
            const { indicatorId, indicatorSchemeId } = state$.value.indicators

            // indicators and indicator dashboards can be fetched three different ways
            // on the dashboard we use indicatorSchemeId, because multiple of those can be linked to the page
            // on workspace cover page it can be used by the workspace id or indicator id
            // workspace id is useful when the indicators are linked through a list and not through an indicator dashboard.
            if (indicatorId > 0) {
                return from(
                    indicatorAPI.getIndicatorsByIndicatorId(indicatorId),
                )
            } else if (indicatorSchemeId > 0) {
                return from(
                    indicatorAPI.getIndicatorsByIndicatorSchemeId(
                        indicatorSchemeId,
                    ),
                )
            } else if (workspaceId > 0) {
                return from(
                    indicatorAPI.getIndicatorsByWorkspaceId(workspaceId),
                )
            }
            return from(new Promise((resolve) => resolve({ MyIndicators: [] })))
        }),
        flatMap((result: IndicatorsResponse) =>
            of(actions.receiveIndicators(result.MyIndicators)),
        ),
        catchError((error) => {
            if (error instanceof RequestCancelledError) {
                return EMPTY
            }
            console.error(error)
            return concat(
                of(actions.receiveIndicators([])),
                of(addErrorNotification(defaultGetIndicatorsErrorMessage)),
            )
        }),
    )

export const requestIndicatorEpic: Epic = (
    action$,
    state$: StateObservable<GlobalState>,
    { indicators: { indicatorAPI, loadingDelay } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_INDICATOR),
        debounceTime(loadingDelay),
        switchMap(() =>
            from(
                indicatorAPI.getIndicatorChartData(
                    state$.value.indicators.indicatorId,
                ),
            ),
        ),
        flatMap((result: IIndicatorData) =>
            of(actions.receiveIndicator(result)),
        ),
        catchError((error) => {
            if (error instanceof RequestCancelledError) {
                return EMPTY
            }
            console.error(error)
            return concat(
                of(actions.receiveIndicatorError()),
                of(addErrorNotification(defaultGetIndicatorErrorMessage)),
            )
        }),
    )

export default combineEpics(requestIndicatorsEpic, requestIndicatorEpic)
